import { type AuthenticationResult, EventType } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import './index.css';
import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import { msalInstance } from './msal';
import { shouldRetryQuery } from './utils/queryError';

const CHUNK_RELOAD_KEY = 'lexo-chunk-reload';

// Recover from deploy version-skew (stale tab requesting a deleted hashed asset).
// Reload at most once per tab session; clear the guard only after the app has
// stayed healthy so a later deploy can recover again without looping forever.
window.addEventListener('vite:preloadError', (event) => {
    event.preventDefault();
    if (!sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
        sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
        window.location.reload();
    }
});
window.setTimeout(() => {
    sessionStorage.removeItem(CHUNK_RELOAD_KEY);
}, 10_000);

Sentry.init({
    dsn: 'https://47754b755b35f131398d3711f3fe0b48@o4510432549076992.ingest.us.sentry.io/4510432550518784',
    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    sendDefaultPii: true,
    // Enable logs to be sent to Sentry
    enableLogs: true,
    environment: import.meta.env.MODE,
    enabled: !window.location.href.includes('localhost'),
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
});

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            retry: shouldRetryQuery,
        },
    },
});

// Default to using the first account if no account is active on page load
if (!msalInstance.getActiveAccount() && msalInstance.getAllAccounts().length > 0) {
    // Account selection logic is app dependent. Adjust as needed for different use cases.
    msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
}

const AUTH_SUCCESS_EVENTS = [
    EventType.LOGIN_SUCCESS,
    EventType.ACQUIRE_TOKEN_SUCCESS,
    EventType.SSO_SILENT_SUCCESS,
];

msalInstance.addEventCallback((event) => {
    if (
        AUTH_SUCCESS_EVENTS.includes(event.eventType) &&
        (event.payload as AuthenticationResult | null)?.account
    ) {
        msalInstance.setActiveAccount((event.payload as AuthenticationResult).account);
    }
});

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
    <MsalProvider instance={msalInstance}>
        <QueryClientProvider client={queryClient}>
            <BrowserRouter>
                <App />
            </BrowserRouter>
        </QueryClientProvider>
    </MsalProvider>,
);
